import { useEffect, useState } from "react"
import { motion } from "framer-motion"
import { X, Trash2, CheckCircle, XCircle } from "lucide-react"
import {
  updateAppointment,
  deleteAppointment,
  getAvailableClients,
  checkAvailability,
} from "../../../api/appointments"
import type { Appointment, Client } from "../../../types"

export default function EditAppointmentModal({
  appointment,
  onClose,
  onUpdated,
  onDeleted,
}: {
  appointment: Appointment
  onClose: () => void
  onUpdated: (a: Appointment) => void
  onDeleted: (id: number) => void
}) {
  const [status, setStatus] = useState(appointment.status)
  const startDate = appointment.start_time.slice(0, 10)
  const [date, setDate] = useState(startDate)
  const [startTime, setStartTime] = useState(
    new Date(appointment.start_time).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    })
  )
  const [endTime, setEndTime] = useState(
    new Date(appointment.end_time).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    })
  )
  const [notes, setNotes] = useState(appointment.notes ?? "")
  const [location, setLocation] = useState(appointment.location ?? "")
  const [selectedIds, setSelectedIds] = useState<number[]>(
    appointment.participants.map((p) => p.id)
  )
  const [clients, setClients] = useState<Client[]>([])
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    getAvailableClients().then(setClients).catch(() => {})
  }, [appointment.appointment_type])

  const maxParticipants = appointment.appointment_type === "personal" ? 1 : 4

  function toggleClient(id: number) {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id)
      if (appointment.appointment_type === "personal" && prev.length >= 1) return [id]
      if (prev.length >= 4) return prev
      return [...prev, id]
    })
  }

  // Auto-generate title from selected participant names
  const autoTitle = selectedIds
    .map((id) => clients.find((c) => c.id === id)?.name)
    .filter(Boolean)
    .join(", ") || "Cita"

  const isValid = date && startTime && endTime && endTime > startTime

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!isValid) return
    setSaving(true)
    setError(null)
    try {
      // Check availability for each hour in the appointment range
      const startH = parseInt(startTime.split(":")[0], 10)
      const endH = parseInt(endTime.split(":")[0], 10)
      for (let h = startH; h < endH; h++) {
        const { available } = await checkAvailability({
          date,
          hour: h,
          exclude_appointment_id: appointment.id,
        })
        if (!available) {
          setError("No hay campo para este horario, seleccione otro")
          setSaving(false)
          return
        }
      }
      const updated = await updateAppointment(appointment.id, {
        title: autoTitle,
        status,
        start_time: `${date}T${startTime}:00`,
        end_time: `${date}T${endTime}:00`,
        notes: notes || undefined,
        location: location || undefined,
        participant_ids: selectedIds,
      })
      onUpdated(updated)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { errors?: string[] } } })?.response?.data
        ?.errors?.[0]
      setError(msg ?? "Error al actualizar la cita.")
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    setDeleting(true)
    try {
      await deleteAppointment(appointment.id)
      onDeleted(appointment.id)
    } catch {
      setError("Error al eliminar la cita.")
      setDeleting(false)
    }
  }

  const statusOptions = [
    { value: "scheduled", label: "Agendada", icon: <CheckCircle size={12} />, color: "text-primary" },
    { value: "completed", label: "Completada", icon: <CheckCircle size={12} />, color: "text-emerald-400" },
    { value: "cancelled", label: "Cancelada", icon: <XCircle size={12} />, color: "text-red-400" },
  ] as const

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md rounded-2xl border border-white/10 gradient-card p-6 shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">Editar Cita</h2>
            <p className="text-xs text-muted">
              {appointment.appointment_type === "personal" ? "Personal" : "Grupal"} — {startDate}
            </p>
          </div>
          <button onClick={onClose} className="text-muted hover:text-white transition text-xl leading-none">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Status */}
          <div>
            <label className="text-xs font-medium text-muted mb-1.5 block">Estado</label>
            <div className="grid grid-cols-3 gap-2">
              {statusOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setStatus(opt.value)}
                  className={`flex items-center justify-center gap-1 rounded-lg border py-1.5 text-xs font-medium transition ${
                    status === opt.value
                      ? `border-current ${opt.color} bg-white/5`
                      : "border-white/10 text-muted hover:text-white"
                  }`}
                >
                  {opt.icon}
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Date + Times */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs font-medium text-muted mb-1.5 block">Fecha</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white focus:border-primary focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted mb-1.5 block">Inicio</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white focus:border-primary focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted mb-1.5 block">Fin</label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white focus:border-primary focus:outline-none"
              />
            </div>
          </div>
          {endTime && startTime && endTime <= startTime && (
            <p className="text-xs text-danger">La hora de fin debe ser posterior a la de inicio</p>
          )}

          {/* Participants */}
          <div>
            <label className="text-xs font-medium text-muted mb-1.5 block">
              Participantes (máx {maxParticipants})
            </label>
            <div className="max-h-36 overflow-y-auto space-y-1 rounded-lg border border-white/10 bg-white/[0.02] p-2">
              {clients.length === 0 ? (
                <p className="text-xs text-muted py-2 text-center">Cargando clientes...</p>
              ) : (
                clients.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleClient(c.id)}
                    className={`w-full flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm transition ${
                      selectedIds.includes(c.id)
                        ? "bg-primary/10 border border-primary/30 text-primary"
                        : "text-muted hover:text-white hover:bg-white/5"
                    }`}
                  >
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/10 text-[10px] font-bold">
                      {c.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .toUpperCase()
                        .slice(0, 2)}
                    </span>
                    {c.name}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="text-xs font-medium text-muted mb-1.5 block">Ubicación</label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Ej: Sala 1"
              className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder-white/30 focus:border-primary focus:outline-none"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs font-medium text-muted mb-1.5 block">Notas</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder-white/30 focus:border-primary focus:outline-none resize-none"
            />
          </div>

          {/* Error */}
          {error && (
            <p className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
              {error}
            </p>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={saving || !isValid}
              className="flex-1 rounded-lg bg-primary py-2.5 text-sm font-semibold text-black hover:opacity-90 transition disabled:opacity-50"
            >
              {saving ? "Guardando..." : "Guardar Cambios"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-white/10 px-4 py-2.5 text-sm text-muted hover:text-white transition"
            >
              Cancelar
            </button>
          </div>

          {/* Delete */}
          <div className="border-t border-white/10 pt-3">
            {!confirmDelete ? (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="flex items-center gap-2 text-xs text-danger hover:text-red-300 transition"
              >
                <Trash2 size={12} />
                Eliminar cita
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <p className="text-xs text-danger flex-1">¿Eliminar esta cita?</p>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="rounded-lg bg-danger/20 border border-danger/30 px-3 py-1.5 text-xs text-danger hover:bg-danger/30 transition disabled:opacity-50"
                >
                  {deleting ? "Eliminando..." : "Confirmar"}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  className="text-xs text-muted hover:text-white transition"
                >
                  No
                </button>
              </div>
            )}
          </div>
        </form>
      </motion.div>
    </div>
  )
}
